import { Animation, CircleEase, EasingFunction, ElasticEase, IAnimationKey, QuadraticEase, SineEase, Vector3 } from '@babylonjs/core';
import { Animation2D } from 'Api/Contracts/Dtos/Project/Renderer';
import { BeatAnimation, BeatFadeAnimation, BounceAnimation, FadeAnimation, FlipAnimation, ShakeAnimation, SpinAnimation } from 'App/Helpers/Animations';

export function isBeatAnimation(animation: Animation2D): animation is BeatAnimation {
    return animation?.type === 'beatAnimation';
}

export function isFadeAnimation(animation: Animation2D): animation is FadeAnimation {
    return animation?.type === 'fadeAnimation';
}

export function isBounceAnimation(animation: Animation2D): animation is BounceAnimation {
    return animation?.type === 'bounceAnimation';
}

export function isFlipAnimation(animation: Animation2D): animation is FlipAnimation {
    return animation?.type === 'flipAnimation';
}

export function isShakeAnimation(animation: Animation2D): animation is ShakeAnimation {
    return animation?.type === 'shakeAnimation';
}

export function isSpinAnimation(animation: Animation2D): animation is SpinAnimation {
    return animation?.type === 'spinAnimation';
}

export function isBeatFadeAnimation(animation: Animation2D): animation is BeatFadeAnimation {
    return animation?.type === 'beatFadeAnimation';
}

export function createAnimation(animation: Animation2D): Array<Animation> {
    if (isBeatAnimation(animation)) {
        return [createBeatAnimation(animation)];
    }
    else if (isFadeAnimation(animation)) {
        return [createFadeAnimation(animation)];
    }
    else if (isBounceAnimation(animation)) {
        return createBounceAnimation(animation);
    }
    else if (isFlipAnimation(animation)) {
        return [createFlipAnimation(animation)];
    }
    else if (isShakeAnimation(animation)) {
        return [createShakeAnimation(animation)];
    }
    else if (isSpinAnimation(animation)) {
        return [createSpinAnimation(animation)];
    }
    else if (isBeatFadeAnimation(animation)) {
        return createBeatFadeAnimation(animation);
    }
    else {
        throw new Error('Invalid Animation');
    }
}

function createBeatAnimation(settings: BeatAnimation): Animation {
    const animation: Animation = new Animation(
        settings.type,
        'scaling',
        settings.frameRate,
        Animation.ANIMATIONTYPE_VECTOR3,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const keyFrames: Array<IAnimationKey> = [
        { frame: 0, value: new Vector3(settings.minimalScale, settings.minimalScale, settings.minimalScale) },
        { frame: settings.frameRate / 2, value: new Vector3(settings.maximalScale, settings.maximalScale, settings.maximalScale) },
        { frame: settings.frameRate, value: new Vector3(settings.minimalScale, settings.minimalScale, settings.minimalScale) }
    ];

    animation.setKeys(keyFrames);

    const easingFunction: SineEase = new SineEase();

    easingFunction.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

    animation.setEasingFunction(easingFunction);

    return animation;
}

function createFadeAnimation(settings: FadeAnimation): Animation {
    const animation: Animation = new Animation(
        settings.type,
        'material.alpha',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const keyFrames: Array<IAnimationKey> = [
        { frame: 0, value: 1 },
        { frame: settings.frameRate / 2, value: settings.opacity },
        { frame: settings.frameRate, value: 1 }
    ];

    animation.setKeys(keyFrames);

    const easingFunction: QuadraticEase = new QuadraticEase();
    easingFunction.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

    animation.setEasingFunction(easingFunction);

    return animation;
}

function createBounceAnimation(settings: BounceAnimation): Array<Animation> {
    const positionAnimation: Animation = new Animation(
        'bounceAnimation_position',
        'position.y',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const positionKeys: Array<IAnimationKey> = [
        { frame: 0, value: 1 },
        { frame: settings.frameRate * 0.2, value: 12 },
        { frame: settings.frameRate * 0.4, value: 1.5 },
        { frame: settings.frameRate * 0.6, value: 5 },
        { frame: settings.frameRate * 0.8, value: 1 },
        { frame: settings.frameRate, value: 1 }
    ];

    positionAnimation.setKeys(positionKeys);

    const scaleXAnimation: Animation = new Animation(
        'bounceAnimation_scaleX',
        'scaling.x',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const scaleXKeys: Array<IAnimationKey> = [
        { frame: 0, value: 1 },
        { frame: settings.frameRate * 0.2, value: 0.8 },
        { frame: settings.frameRate * 0.4, value: 1.1 },
        { frame: settings.frameRate * 0.6, value: 0.9 },
        { frame: settings.frameRate * 0.8, value: 1 },
        { frame: settings.frameRate, value: 1 }
    ];

    scaleXAnimation.setKeys(scaleXKeys);

    const scaleYAnimation: Animation = new Animation(
        'bounceAnimation_scaleY',
        'scaling.y',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const scaleYKeys: Array<IAnimationKey> = [
        { frame: 0, value: 1 },
        { frame: settings.frameRate * 0.2, value: 1.2 },
        { frame: settings.frameRate * 0.4, value: 0.9 },
        { frame: settings.frameRate * 0.6, value: 1.1 },
        { frame: settings.frameRate * 0.8, value: 1 },
        { frame: settings.frameRate, value: 1 }
    ];

    scaleYAnimation.setKeys(scaleYKeys);

    return [positionAnimation, scaleXAnimation, scaleYAnimation];
}

function createFlipAnimation(settings: FlipAnimation): Animation {
    const animation: Animation = new Animation(
        settings.type,
        settings.rotationAxe == 'horizontal'
            ? 'rotation.x'
            : 'rotation.y',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const keyFrames: Array<IAnimationKey> = [
        { frame: 0, value: 0 },
        { frame: settings.frameRate / 2, value: -Math.PI },
        { frame: settings.frameRate, value: -Math.PI * 2 }
    ];

    animation.setKeys(keyFrames);

    const easingFunction: CircleEase = new CircleEase();
    easingFunction.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

    animation.setEasingFunction(easingFunction);

    return animation;
}

function createShakeAnimation(settings: ShakeAnimation): Animation {
    const animation: Animation = new Animation(
        settings.type,
        'rotation.z',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const keyFrames: Array<IAnimationKey> = [];
    const maxAmplitude: number = 0.17;
    const oscillations: number = 6;
    const totalFrames: number = oscillations * 2;

    for (let i = 0; i <= totalFrames; i++) {
        const frame: number = (i * settings.frameRate) / totalFrames;

        const amplitude: number = maxAmplitude * (1 - i / totalFrames);

        const value: number = i % 2 === 0 ? amplitude : -amplitude;

        keyFrames.push({
            frame: frame,
            value: value
        });
    }

    keyFrames.push({
        frame: settings.frameRate,
        value: 0
    });

    animation.setKeys(keyFrames);

    const easingFunction: ElasticEase = new ElasticEase();

    easingFunction.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);
    easingFunction.oscillations = 1;
    easingFunction.springiness = 0.3;

    animation.setEasingFunction(easingFunction);

    return animation;
}

function createSpinAnimation(settings: SpinAnimation): Animation {
    const animation: Animation = new Animation(
        settings.type,
        'rotation.z',
        settings.frameRate,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE);

    const keyFrames: Array<IAnimationKey> = [
        { frame: 0, value: 0 },
        { frame: settings.frameRate, value: -2 * Math.PI }
    ];

    animation.setKeys(keyFrames);

    return animation;
}

function createBeatFadeAnimation(settings: BeatFadeAnimation): Array<Animation> {
    const beatAnimation: Animation = createBeatAnimation({
        type: 'beatAnimation',
        maximalScale: settings.maximalScale,
        minimalScale: settings.minimalScale,
        frameRate: settings.frameRate,
        animationSpeed: settings.animationSpeed
    });

    const fadeAnimation: Animation = createFadeAnimation({
        type: 'fadeAnimation',
        opacity: settings.opacity,
        frameRate: settings.frameRate,
        animationSpeed: settings.animationSpeed
    });

    return [beatAnimation, fadeAnimation];
}
